
import React, { useState, useRef, useEffect } from 'react';
import './AiHubPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import Card from '../../components/shared/Card';
import { useToast } from '../../context/ToastContext';

type Model = 'gpt-4o' | 'claude-3-5-sonnet' | 'gemini-pro';
type Mode = 'chat' | 'image' | 'code' | 'summarize';

const MODELS: { id: Model; label: string; provider: string; color: string }[] = [
  { id: 'gpt-4o', label: 'GPT-4o', provider: 'OpenAI', color: '#10A37F' },
  { id: 'claude-3-5-sonnet', label: 'Claude 3.5', provider: 'Anthropic', color: '#C09853' },
  { id: 'gemini-pro', label: 'Gemini Pro', provider: 'Google', color: '#4285F4' },
];

const MODES: { id: Mode; label: string; icon: string }[] = [
  { id: 'chat', label: 'Chat', icon: '💬' },
  { id: 'image', label: 'Image Gen', icon: '🎨' },
  { id: 'code', label: 'Code', icon: '💻' },
  { id: 'summarize', label: 'Summarize', icon: '📝' },
];

const PROMPT_TEMPLATES = [
  { label: 'Write a product brief', prompt: 'Write a concise product brief for a new SaaS feature that...' },
  { label: 'Debug this code', prompt: 'Here is my code, please identify any bugs and suggest fixes:\n\n```\n// paste code here\n```' },
  { label: 'Summarize document', prompt: 'Please summarize the following document into key bullet points:\n\n' },
  { label: 'Generate email', prompt: 'Draft a professional follow-up email for a client meeting about...' },
  { label: 'SQL query', prompt: 'Write a SQL query to select all users who have signed up in the last 30 days and have made at least one purchase.' },
  { label: 'Explain concept', prompt: 'Explain the concept of WebSockets in simple terms suitable for a non-technical stakeholder.' },
];

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tokens?: number;
}

function generateResponse(userMsg: string, model: Model, mode: Mode): string {
  const responses: Record<Mode, string[]> = {
    chat: [
      `Based on your query, here's my analysis:\n\n**Key Insights**\n• The approach you've described aligns well with modern best practices\n• Consider implementing a phased rollout to minimize risk\n• Documentation will be critical for team adoption\n\nWould you like me to elaborate on any of these points?`,
      `Great question! Let me break this down:\n\n1. **First**, understand the core problem space\n2. **Then**, identify the stakeholders involved\n3. **Finally**, propose a measurable solution\n\nThe data suggests a 34% improvement in efficiency when following this framework.`,
      `I've analyzed your request carefully. Here are my recommendations:\n\n> The most effective approach combines automation with human oversight\n\nThis ensures quality while scaling efficiently. Would you like a detailed implementation plan?`,
    ],
    code: [
      "Here's a clean implementation:\n\n```typescript\ninterface ApiResponse<T> {\n  data: T;\n  status: number;\n  message: string;\n}\n\nasync function fetchData<T>(url: string): Promise<ApiResponse<T>> {\n  const res = await fetch(url);\n  if (!res.ok) throw new Error(`HTTP ${res.status}`);\n  return res.json();\n}\n\n// Usage\nconst users = await fetchData<User[]>('/api/users');\n```\n\nThis uses TypeScript generics for type safety and proper error handling.",
      "```python\nimport asyncio\nfrom dataclasses import dataclass\nfrom typing import List\n\n@dataclass\nclass DataPoint:\n    timestamp: float\n    value: float\n    label: str\n\nasync def process_stream(data: List[DataPoint]) -> dict:\n    results = {}\n    for point in data:\n        if point.label not in results:\n            results[point.label] = []\n        results[point.label].append(point.value)\n    return {k: sum(v)/len(v) for k, v in results.items()}\n```",
    ],
    image: [
      "🎨 **Image Generation Request Queued**\n\nYour prompt has been processed. Here's what the generated image would depict:\n\n• Style: Photorealistic digital art\n• Resolution: 1024×1024px\n• Estimated render time: ~12 seconds\n• Format: PNG with transparency support\n\n*In production, this would display the actual generated image via DALL·E 3 or Stable Diffusion.*",
    ],
    summarize: [
      "**Summary Complete** ✓\n\n**Key Points:**\n• Main theme focuses on operational efficiency and scalability\n• Three primary action items identified requiring immediate attention\n• Timeline suggests Q2 2026 deployment window\n• Budget allocation: 40% infrastructure, 35% development, 25% operations\n\n**Sentiment:** Positive with cautious optimism\n**Reading Level:** Executive summary — suitable for C-suite presentation\n**Word Count:** ~2,400 words condensed to 8 key bullets",
    ],
  };

  const pool = responses[mode] || responses.chat;
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function AiHubPage() {
  const [model, setModel] = useState<Model>('gpt-4o');
  const [mode, setMode] = useState<Mode>('chat');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [showSettings, setShowSettings] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { addToast } = useToast();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setLoading(true);

    await new Promise(r => setTimeout(r, 900 + Math.random() * 600));

    const responseText = generateResponse(input, model, mode);
    let streamed = '';
    const aiMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      tokens: Math.floor(Math.random() * 400 + 100),
    };

    setMessages(m => [...m, aiMsg]);
    setLoading(false);

    for (let i = 0; i < responseText.length; i += 4) {
      await new Promise(r => setTimeout(r, 18));
      streamed = responseText.slice(0, i + 4);
      setMessages(m => m.map(msg => msg.id === aiMsg.id ? { ...msg, content: streamed } : msg));
    }
    setMessages(m => m.map(msg => msg.id === aiMsg.id ? { ...msg, content: responseText } : msg));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearChat = () => {
    setMessages([]);
    addToast('info', 'Chat cleared');
  };

  const useTemplate = (prompt: string) => {
    setInput(prompt);
    textareaRef.current?.focus();
  };

  const activeModel = MODELS.find(m => m.id === model)!;

  return (
    <div className="ai-hub-page">
      <div className="ai-hub-layout">
        {/* Left sidebar */}
        <div className="ai-hub-sidebar">
          <div className="ai-hub-sidebar__section">
            <div className="ai-hub-sidebar__label">Model</div>
            {MODELS.map(m => (
              <button
                key={m.id}
                className={`ai-model-btn${model === m.id ? ' ai-model-btn--active' : ''}`}
                style={model === m.id ? { borderColor: m.color + '55', background: m.color + '11' } : {}}
                onClick={() => setModel(m.id)}
              >
                <span className="ai-model-btn__dot" style={{ background: m.color }} />
                <div className="ai-model-btn__info">
                  <span className="ai-model-btn__name">{m.label}</span>
                  <span className="ai-model-btn__provider">{m.provider}</span>
                </div>
                {model === m.id && <span className="ai-model-btn__check" style={{ color: m.color }}>✓</span>}
              </button>
            ))}
          </div>

          <div className="ai-hub-sidebar__section">
            <div className="ai-hub-sidebar__label">Mode</div>
            {MODES.map(m => (
              <button
                key={m.id}
                className={`ai-mode-btn${mode === m.id ? ' ai-mode-btn--active' : ''}`}
                onClick={() => setMode(m.id)}
              >
                <span>{m.icon}</span>
                <span>{m.label}</span>
              </button>
            ))}
          </div>

          <div className="ai-hub-sidebar__section">
            <button
              className="ai-settings-toggle"
              onClick={() => setShowSettings(s => !s)}
            >
              ⚙ Parameters {showSettings ? '▲' : '▼'}
            </button>
            {showSettings && (
              <div className="ai-params">
                <label className="ai-param-label">
                  Temperature: <strong>{temperature}</strong>
                  <input
                    type="range" min="0" max="2" step="0.1" value={temperature}
                    onChange={e => setTemperature(Number(e.target.value))}
                    className="ai-param-slider"
                    style={{ accentColor: activeModel.color }}
                  />
                </label>
                <label className="ai-param-label">
                  Max Tokens: <strong>{maxTokens}</strong>
                  <input
                    type="range" min="128" max="4096" step="128" value={maxTokens}
                    onChange={e => setMaxTokens(Number(e.target.value))}
                    className="ai-param-slider"
                    style={{ accentColor: activeModel.color }}
                  />
                </label>
              </div>
            )}
          </div>

          <div className="ai-hub-sidebar__section">
            <div className="ai-hub-sidebar__label">Templates</div>
            <div className="ai-templates">
              {PROMPT_TEMPLATES.map(t => (
                <button key={t.label} className="ai-template-btn" onClick={() => useTemplate(t.prompt)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chat area */}
        <div className="ai-chat-area">
          {/* Chat header */}
          <div className="ai-chat-header">
            <div className="ai-chat-header__left">
              <div className="ai-model-indicator" style={{ borderColor: activeModel.color + '55' }}>
                <span className="ai-model-indicator__dot" style={{ background: activeModel.color }} />
                <span>{activeModel.label}</span>
                <Badge variant="default">{mode}</Badge>
              </div>
            </div>
            <div className="ai-chat-header__right">
              {messages.length > 0 && (
                <button className="ai-chat-action-btn" onClick={clearChat}>Clear</button>
              )}
              <button className="ai-chat-action-btn" onClick={() => addToast('success', 'Chat exported!')}>Export</button>
            </div>
          </div>

          {/* Messages */}
          <div className="ai-messages">
            {messages.length === 0 && (
              <div className="ai-welcome">
                <div className="ai-welcome__icon" style={{ background: activeModel.color + '22', borderColor: activeModel.color + '44' }}>
                  🤖
                </div>
                <h3>AI Hub — {activeModel.label}</h3>
                <p>Start a conversation, generate code, create images, or summarize documents. Select a template from the sidebar to get started quickly.</p>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`ai-message ai-message--${msg.role}`}>
                <div className="ai-message__avatar">
                  {msg.role === 'user' ? 'JD' : '🤖'}
                </div>
                <div className="ai-message__bubble">
                  <div className="ai-message__content">
                    {msg.content.split('\n').map((line, i) => {
                      if (line.startsWith('```')) return null;
                      if (line.startsWith('**') && line.endsWith('**')) {
                        return <strong key={i} style={{ display: 'block', marginTop: i > 0 ? '8px' : 0 }}>{line.replace(/\*\*/g, '')}</strong>;
                      }
                      if (line.startsWith('•') || line.startsWith('•')) {
                        return <div key={i} style={{ paddingLeft: '12px', marginTop: '4px' }}>{line}</div>;
                      }
                      if (line.startsWith('>')) {
                        return <blockquote key={i} className="ai-blockquote">{line.slice(1).trim()}</blockquote>;
                      }
                      if (line.trim() === '') return <br key={i} />;
                      return <span key={i}>{line}{'\n'}</span>;
                    })}
                  </div>
                  <div className="ai-message__meta">
                    <span>{msg.timestamp.toLocaleTimeString()}</span>
                    {msg.tokens && <span>{msg.tokens} tokens</span>}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="ai-message ai-message--assistant">
                <div className="ai-message__avatar">🤖</div>
                <div className="ai-message__bubble">
                  <div className="ai-typing">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="ai-input-area">
            <div className="ai-input-wrap">
              <textarea
                ref={textareaRef}
                className="ai-input"
                placeholder={`Message ${activeModel.label}… (Enter to send, Shift+Enter for new line)`}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                style={{ accentColor: activeModel.color }}
              />
              <button
                className="ai-send-btn"
                onClick={send}
                disabled={!input.trim() || loading}
                style={{ background: input.trim() && !loading ? activeModel.color : undefined }}
              >
                ▶
              </button>
            </div>
            <div className="ai-input-footer">
              <span>Model: {activeModel.label} · {activeModel.provider}</span>
              <span>Temp: {temperature} · Max: {maxTokens} tokens</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
